// Update-gate for Capacitor iOS. Calls /api/app/version-check on launch,
// compares the shipped build number against the server, and either:
//   - status="ok"           → renders nothing (99% of the time)
//   - status="soft_update"  → floating banner at the top of screen,
//     dismissible for 24 h via `calofay:update_dismissed_until_ms`.
//   - status="force_update" → full-screen blocker (no dismiss button).
//
// Web + Android render nothing — this is iOS-only by design. Web always
// serves the latest bundle, and Android has no native shell yet.
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { X, Download } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";

const DISMISS_KEY = "calofay:update_dismissed_until_ms";
const DISMISS_MS = 24 * 60 * 60 * 1000;                          // 24 h

async function _getIosBuildNumber() {
  // Capacitor App plugin's getInfo() returns `build` as a string on iOS
  // ("10", "11", …) — coerce to int. On web / non-iOS this call is not
  // reached because the caller short-circuits, but we still guard.
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    const n = parseInt(info?.build ?? "0", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function _openAppStore(url) {
  // Prefer the deep-link scheme so iOS opens the App Store app instantly
  // instead of routing through Safari. Falls back to https:// if the
  // scheme is blocked (e.g. Xcode simulator without App Store).
  try {
    const deep = (url || "").replace(/^https?:\/\/apps\.apple\.com/, "itms-apps://apps.apple.com");
    window.location.href = deep || url;
  } catch {
    if (url) window.location.href = url;
  }
}

export default function UpdateGate() {
  const { lang } = useI18n();
  const en = lang === "en";
  const [state, setState] = useState(null);   // null | "soft" | "force"
  const [storeUrl, setStoreUrl] = useState("");
  const [latestBuild, setLatestBuild] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Bail immediately on web / Android — this gate is iOS-only.
        const isNativeIOS =
          Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
        if (!isNativeIOS) return;

        const build = await _getIosBuildNumber();
        if (build <= 0) return;                // don't nag builds we can't identify

        const { data } = await api.get(
          `/app/version-check?platform=ios&build=${build}`
        );
        if (cancelled) return;
        if (!data || data.status === "ok") return;

        setLatestBuild(data.latest_build || 0);
        setStoreUrl(data.store_url || "");

        if (data.status === "force_update") {
          setState("force");
          return;
        }

        // Soft update: respect the 24 h dismiss cookie. `dismissedUntil`
        // is stored per-installed-latest-build so if the admin bumps the
        // build number AGAIN mid-dismissal, the banner reappears on the
        // next launch (fresh version = fresh nag).
        const dismissRaw = localStorage.getItem(DISMISS_KEY);
        if (dismissRaw) {
          const [untilStr, forBuildStr] = dismissRaw.split(":");
          const until = parseInt(untilStr, 10);
          const forBuild = parseInt(forBuildStr, 10);
          if (
            Number.isFinite(until) &&
            Date.now() < until &&
            forBuild === (data.latest_build || 0)
          ) {
            return;   // still within the 24 h window for THIS build → stay quiet
          }
        }
        setState("soft");
      } catch (e) {
        // Any failure here is silent — we don't want a broken version
        // check to break app launch. The user will just miss the nudge.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        `${Date.now() + DISMISS_MS}:${latestBuild}`
      );
    } catch {}
    setState(null);
  };

  if (state === null) return null;

  // ── Full-screen force update ───────────────────────────────────────
  if (state === "force") {
    return (
      <div
        className="fixed inset-0 z-[200] bg-white flex flex-col items-center justify-center px-6 text-center"
        dir={en ? "ltr" : "rtl"}
        data-testid="update-gate-force"
      >
        <div className="w-16 h-16 rounded-3xl orange-gradient text-white flex items-center justify-center mb-4 shadow-brand">
          <Download className="w-8 h-8" />
        </div>
        <div className="text-2xl font-black text-neutral-900 mb-2">
          {en ? "Update required" : "التحديث مطلوب"}
        </div>
        <div className="text-sm text-neutral-600 max-w-xs mb-6 leading-relaxed">
          {en
            ? "Please update Calofay from the App Store to continue using the app."
            : "يرجى تحديث كالوفاي من متجر التطبيقات للمتابعة."}
        </div>
        <button
          onClick={() => _openAppStore(storeUrl)}
          className="orange-gradient text-white font-bold px-8 py-3 rounded-full shadow-brand"
          data-testid="update-gate-force-btn"
        >
          {en ? "Update in App Store" : "التحديث من App Store"}
        </button>
      </div>
    );
  }

  // ── Soft-update banner ─────────────────────────────────────────────
  return (
    <div
      role="status"
      aria-live="polite"
      dir={en ? "ltr" : "rtl"}
      data-testid="update-gate-soft"
      className="fixed inset-x-0 z-[100] pointer-events-none flex justify-center px-3"
      style={{
        top: "env(safe-area-inset-top)",
        paddingTop: 8,
      }}
    >
      <div className="pointer-events-auto flex items-center gap-2 max-w-sm w-full bg-neutral-900 text-white shadow-lg rounded-2xl px-3 py-2.5 border border-white/10 backdrop-blur">
        <div className="w-8 h-8 rounded-xl bg-brand-500 text-white flex items-center justify-center flex-shrink-0">
          <Download className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold leading-tight">
            {en ? "New version available" : "يوجد إصدار جديد"}
          </div>
          <div className="text-[10px] opacity-70 leading-tight mt-0.5 truncate">
            {en
              ? "Tap Update to install the latest Calofay"
              : "اضغط للتحديث لتثبيت آخر إصدار من كالوفاي"}
          </div>
        </div>
        <button
          onClick={() => _openAppStore(storeUrl)}
          className="flex-shrink-0 bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors active:scale-95"
          data-testid="update-gate-open-store"
        >
          {en ? "Update" : "تحديث"}
        </button>
        <button
          onClick={dismiss}
          className="flex-shrink-0 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors active:scale-95"
          aria-label={en ? "Dismiss for today" : "إخفاء اليوم"}
          data-testid="update-gate-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
